import { action, makeObservable, observable } from "mobx";
import { DefaultRequestStatus, RequestStatus } from "../../shared/api/RequestStatus";
import customerMeetingsService, {
    CreateCustomerMeetingData,
    CustomerMeetingsService,
    MeetingTypeDictionaryItemDto,
    UpdateCustomerMeetingData,
    UpdateCustomerMeetingTypeData,
} from "../../customerMeetings/service/CustomerMeetingsService";
import { CustomerMeeting } from "./CustomerMeeting";
import { CustomerMeetingDto } from "../service/api/CustomerMeetingDto";
import { CustomerMeetingFromResponse } from "../service/CustomerMeetingFromResponse";
import { MeetingLogType } from "./MeetingLogType";
import { MeetingDeclineReason } from "../../shared/types/MeetingDeclineReason";
import { MeetingType } from "./MeetingType";
import { MeetingTypeOption } from "./MeetingTypeOption";
import { MeetingTypeDictionaryEntryFromResponse } from "../../inMeetingApp/service/MeetingTypeDictionaryEntryFromResponse";
import { MeetingOptions } from "./MeetingOptions";

export interface MeetingStore {
    meetings: CustomerMeeting[];
    meeting: CustomerMeeting;
    meetingType: MeetingType;
    meetingOptions: MeetingOptions;
    createMeetingRequestStatus: RequestStatus;
    cancelMeetingRequestStatus: RequestStatus;
    updateMeetingRequestStatus: RequestStatus;
    updateMeetingTypeRequestStatus: RequestStatus;
    loadMeetingsRequestStatus: RequestStatus;
    getMeetingTypeRequestStatus: RequestStatus;
    addLogRequestStatus: RequestStatus;

    cancelMeeting: (urlHash: string, reason: MeetingDeclineReason) => void;
    createMeeting: (createCustomerMeetingData: CreateCustomerMeetingData) => void;
    updateMeeting: (updateCustomerMeetingData: UpdateCustomerMeetingData) => Promise<void>;
    updateMeetingType: (updateCustomerMeetingTypeData: UpdateCustomerMeetingTypeData) => Promise<void>;
    loadMeetings: () => void;
    loadMeetingOptions: (language: string) => void;
    getMeetingType: (meetingId: string) => void;
    addLog: (meetingId: string, logType: MeetingLogType) => void;
}

export class DefaultMeetingStore implements MeetingStore {
    constructor(
        private service: CustomerMeetingsService,
        private createMeetingReqStatus: RequestStatus,
        private cancelMeetingReqStatus: RequestStatus,
        private updateMeetingReqStatus: RequestStatus,
        private loadMeetingsReqStatus: RequestStatus,
        private loadMeetingOptionsReqStatus: RequestStatus,
        private getMeetingTypeReqStatus: RequestStatus,
        private addLogReqStatus: RequestStatus,
        private updateMeetingTypeReqStatus: RequestStatus
    ) {
        makeObservable(this, {
            createMeeting: action,
            cancelMeeting: action,
            updateMeeting: action,
            updateMeetingType: action,
            loadMeetings: action,
            loadMeetingOptions: action,
            getMeetingType: action,
            meetings: observable,
            meeting: observable,
            meetingType: observable,
            meetingOptions: observable,
        });
    }

    public meetings: CustomerMeeting[] = [];

    public meeting: CustomerMeeting = {} as CustomerMeeting;

    public meetingType: MeetingType = {} as MeetingType;

    public meetingOptions: MeetingOptions = {} as MeetingOptions;

    public get cancelMeetingRequestStatus() {
        return this.cancelMeetingReqStatus;
    }

    public get createMeetingRequestStatus() {
        return this.createMeetingReqStatus;
    }

    public get updateMeetingRequestStatus() {
        return this.updateMeetingReqStatus;
    }

    public get updateMeetingTypeRequestStatus() {
        return this.updateMeetingTypeReqStatus;
    }

    public get loadMeetingsRequestStatus() {
        return this.loadMeetingsReqStatus;
    }

    public get loadMeetingOptionsRequestStatus() {
        return this.loadMeetingOptionsReqStatus;
    }

    public get getMeetingTypeRequestStatus() {
        return this.getMeetingTypeReqStatus;
    }

    public get addLogRequestStatus() {
        return this.addLogReqStatus;
    }

    public async cancelMeeting(urlHash: string, reason: MeetingDeclineReason) {
        return await this.cancelMeetingReqStatus.handleAnyRequest(() =>
            this.service.cancelMeeting({ urlHash: urlHash, reason: reason })
        );
    }

    public async createMeeting(createCustomerMeetingData: CreateCustomerMeetingData) {
        await this.createMeetingReqStatus.handleAnyRequest(() => this.service.createMeeting(createCustomerMeetingData));

        this.loadMeetings();
    }

    public async updateMeeting(updateCustomerMeetingData: UpdateCustomerMeetingData) {
        await this.updateMeetingReqStatus.handleAnyRequest(() => this.service.updateMeeting(updateCustomerMeetingData));

        await this.loadMeetings();
    }

    public async updateMeetingType(updateCustomerMeetingTypeData: UpdateCustomerMeetingTypeData) {
        await this.updateMeetingTypeReqStatus.handleAnyRequest(() =>
            this.service.updateMeetingType(updateCustomerMeetingTypeData)
        );

        await this.loadMeetings();
    }

    public async loadMeetings() {
        var fetchedMeetings = await this.loadMeetingsReqStatus.handleAnyRequest(() => this.service.getMeetings());

        if (fetchedMeetings) {
            this.meetings = fetchedMeetings.map((dto: CustomerMeetingDto) =>
                new CustomerMeetingFromResponse(dto).customerMeeting()
            );
        }
    }

    public async loadMeetingOptions(language: string) {
        var fetchedOptions = await this.loadMeetingOptionsReqStatus.handleAnyRequest(() =>
            this.service.getMeetingOptions(language)
        );

        if (fetchedOptions) {
            this.meetingOptions = {
                showMeetingKindToggle: fetchedOptions.showMeetingKindToggle,
                defaultMeetingKind: fetchedOptions.defaultMeetingKind,
                meetingKindList: fetchedOptions.meetingKindList,
                meetingTypeOptionList: fetchedOptions.meetingTypeList.map((dto) => {
                    return {
                        key: dto.key,
                        value: dto.value,
                    };
                }),
                maxNumberOfDaysToPlanMeeting: fetchedOptions.maxNumberOfDaysToPlanMeeting,
            };
        }
    }

    public async getMeetingType(meetingId: string) {
        var fetchedMeetingType = await this.getMeetingTypeReqStatus.handleAnyRequest(() =>
            this.service.getMeetingType(meetingId)
        );
        this.meetingType = { value: fetchedMeetingType?.value };
    }

    public async addLog(meetingId: string, logType: MeetingLogType) {
        await this.addLogReqStatus.handleAnyRequest(() => this.service.addLog({ meetingId, logType }));
    }
}

export default new DefaultMeetingStore(
    customerMeetingsService,
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus(),
    new DefaultRequestStatus()
);
